import Badge from '@/components/base/Badge';
import Button from '@/components/base/Button';
import { formatMoney } from '@/utils/format';
import type { Transaction } from '@/types/portal';

interface NeedsAttentionPanelProps {
  rows: Transaction[];
  onSelect: (row: Transaction) => void;
  onReconcile: () => void;
  /** False when the API sends no reconciliation state, so no line can be flagged. */
  reconciliationAvailable?: boolean;
}

export default function NeedsAttentionPanel({
  rows,
  onSelect,
  onReconcile,
  reconciliationAvailable = true,
}: NeedsAttentionPanelProps) {
  return (
    <section className="rounded-lg border border-primary-200 bg-primary-50">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-primary-200 px-5 py-3.5">
        <div className="flex items-center gap-2">
          <i className="ri-alert-line text-primary-700" />
          <h2 className="font-heading text-sm font-semibold text-primary-800">Needs attention</h2>
          {rows.length > 0 ? (
            <span className="rounded-full bg-primary-200 px-2 py-0.5 text-[11px] font-label font-medium text-primary-800">
              {rows.length}
            </span>
          ) : null}
        </div>
        {rows.length > 0 ? (
          <Button variant="outline" size="sm" icon="ri-refresh-line" onClick={onReconcile}>
            Reconcile all
          </Button>
        ) : null}
      </header>

      {!reconciliationAvailable ? (
        <p className="px-5 py-4 text-sm text-primary-700">Payment checks are coming soon.</p>
      ) : rows.length === 0 ? (
        <p className="px-5 py-4 text-sm text-primary-700">
          Everything matched — nothing outstanding.
        </p>
      ) : (
        <ul className="divide-y divide-primary-100">
          {rows.slice(0, 5).map((row) => (
            <li key={row.id} className="flex items-center justify-between gap-3 px-5 py-3">
              <div className="min-w-0">
                <p className="truncate font-mono text-xs text-primary-700">{row.reference}</p>
                <p className="truncate text-sm font-medium text-foreground-900">{row.party}</p>
                <p className="truncate text-xs text-foreground-600">{row.description}</p>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <div className="text-right">
                  <p className="text-sm text-foreground-900">
                    {formatMoney(row.amount, row.currency)}
                  </p>
                  <Badge tone="primary">{row.recon}</Badge>
                </div>
                <button
                  type="button"
                  onClick={() => onSelect(row)}
                  className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-primary-200 bg-background-50 text-primary-700 transition-colors hover:bg-primary-100"
                  aria-label={`View ${row.reference}`}
                  title="View details"
                >
                  <i className="ri-eye-line" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}